import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Collapse,
  Grid,
  useTheme,
  alpha,
  Badge,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as DraftingCompassIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NewReleases as NewReleasesIcon,
  Work as WorkIcon,
  AccessTime as AccessTimeIcon,
  LocationOff as LocationOffIcon,
  Assignment as AssignmentIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const DefectsFilters = ({ onFiltersChange, defectStats = {}, initialFilters }) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFilters, setActiveFilters] = useState(initialFilters || {
    validationStatus: null,
    defectStatus: null,
    boundary: null,
    workOrder: null,
    sla: null,
  });

  // Filter definitions with icons and descriptions
  const filterCategories = {
    validationStatus: {
      title: 'Validation Status',
      icon: <CheckCircleIcon fontSize="small" />,
      filters: [
        {
          key: 'draft',
          label: 'Draft Only',
          description: 'Findings awaiting validation',
          icon: <DraftingCompassIcon fontSize="small" />,
          color: 'default',
          count: defectStats.draft || 0,
        },
        {
          key: 'validated',
          label: 'Validated',
          description: 'Confirmed defects',
          icon: <CheckCircleIcon fontSize="small" />,
          color: 'success',
          count: defectStats.validated || 0,
        },
        {
          key: 'rejected',
          label: 'Rejected',
          description: 'Findings marked as invalid',
          icon: <CancelIcon fontSize="small" />,
          color: 'error',
          count: defectStats.rejected || 0,
        },
      ],
    },
    defectStatus: {
      title: 'Defect Status',
      icon: <NewReleasesIcon fontSize="small" />,
      filters: [
        {
          key: 'new',
          label: 'New',
          description: 'Recently confirmed defects',
          icon: <NewReleasesIcon fontSize="small" />,
          color: 'info',
          count: defectStats.newDefects || 0,
        },
        {
          key: 'in_progress',
          label: 'In Progress',
          description: 'Defects being repaired',
          icon: <WorkIcon fontSize="small" />,
          color: 'warning',
          count: defectStats.inProgress || 0,
        },
        {
          key: 'resolved',
          label: 'Resolved',
          description: 'Completed repairs',
          icon: <CheckCircleIcon fontSize="small" />,
          color: 'secondary',
          count: defectStats.resolved || 0,
        },
      ],
    },
    workOrder: {
      title: 'Work Orders',
      icon: <AssignmentIcon fontSize="small" />,
      filters: [
        {
          key: 'has_work_order',
          label: 'Has Work Order',
          description: 'Defects with issued work orders',
          icon: <AssignmentIcon fontSize="small" />,
          color: 'primary',
          count: defectStats.withWorkOrders || 0,
        },
        {
          key: 'no_work_order',
          label: 'No Work Order',
          description: 'Defects without work orders',
          icon: <AssignmentIcon fontSize="small" />,
          color: 'default',
          count: defectStats.withoutWorkOrders || 0,
        },
      ],
    },
    sla: {
      title: 'SLA Status',
      icon: <AccessTimeIcon fontSize="small" />,
      filters: [
        {
          key: 'overdue',
          label: 'SLA Exceeded',
          description: 'Defects past due time',
          icon: <AccessTimeIcon fontSize="small" />,
          color: 'error',
          count: defectStats.overdueDefects || 0,
        },
        {
          key: 'due_soon',
          label: 'Due Soon',
          description: 'Defects approaching deadline',
          icon: <AccessTimeIcon fontSize="small" />,
          color: 'warning',
          count: defectStats.dueSoonDefects || 0,
        },
        {
          key: 'on_time',
          label: 'On Time',
          description: 'Defects within SLA',
          icon: <AccessTimeIcon fontSize="small" />,
          color: 'success',
          count: defectStats.onTimeDefects || 0,
        },
      ],
    },
    boundary: {
      title: 'Location Status',
      icon: <LocationOffIcon fontSize="small" />,
      filters: [
        {
          key: 'out_of_boundary',
          label: 'Out of Boundary',
          description: 'Findings outside network boundaries',
          icon: <LocationOffIcon fontSize="small" />,
          color: 'error',
          count: defectStats.outOfBoundary || 0,
        },
        {
          key: 'in_boundary',
          label: 'In Boundary',
          description: 'Findings within network boundaries',
          icon: <CheckCircleIcon fontSize="small" />,
          color: 'success',
          count: defectStats.inBoundary || 0,
        },
      ],
    },
  };

  // Handle filter selection
  const handleFilterSelect = (category, filterKey) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      // Toggle filter - if same filter is clicked, deselect it
      if (newFilters[category] === filterKey) {
        newFilters[category] = null;
      } else {
        newFilters[category] = filterKey;
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const handleClearAll = () => {
    setActiveFilters({
      validationStatus: null,
      defectStatus: null,
      boundary: null,
      workOrder: null,
      sla: null,
    });
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  // Notify parent component of filter changes
  useEffect(() => {
    onFiltersChange(activeFilters);
  }, [activeFilters, onFiltersChange]);

  // Render filter chip
  const renderFilterChip = (filter, category, isActive) => (
    <Tooltip key={filter.key} title={filter.description} arrow>
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.125 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
              {filter.icon}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 500, fontSize: '0.75rem' }}>
              {filter.label}
            </Typography>
            {filter.count > 0 && (
              <Box
                sx={{
                  backgroundColor: isActive 
                    ? alpha(theme.palette.common.white, 0.25) 
                    : alpha(theme.palette.text.secondary, 0.12),
                  color: isActive 
                    ? theme.palette.common.white 
                    : theme.palette.text.secondary,
                  borderRadius: '8px',
                  px: 0.5,
                  py: 0.125,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  minWidth: '16px',
                  textAlign: 'center',
                  lineHeight: 1,
                }}
              >
                {filter.count}
              </Box>
            )}
          </Box>
        }
        onClick={() => handleFilterSelect(category, filter.key)}
        variant="outlined"
        sx={{
          m: 0.25,
          borderRadius: '16px',
          border: `1px solid ${isActive 
            ? theme.palette[filter.color]?.main || theme.palette.primary.main
            : alpha(theme.palette.divider, 0.3)}`,
          backgroundColor: isActive 
            ? theme.palette[filter.color]?.main || theme.palette.primary.main
            : 'transparent',
          color: isActive 
            ? theme.palette.common.white
            : theme.palette.text.primary,
          cursor: 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          height: '28px',
          '& .MuiChip-label': {
            px: 1,
            py: 0.25,
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: isActive 
              ? `0 3px 8px ${alpha(theme.palette[filter.color]?.main || theme.palette.primary.main, 0.3)}`
              : `0 2px 6px ${alpha(theme.palette.grey[900], 0.12)}`,
            border: `1px solid ${theme.palette[filter.color]?.main || theme.palette.primary.main}`,
            backgroundColor: isActive 
              ? theme.palette[filter.color]?.main || theme.palette.primary.main
              : alpha(theme.palette[filter.color]?.main || theme.palette.primary.main, 0.06),
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }}
      />
    </Tooltip>
  );

  // Render filter category
  const renderFilterCategory = (categoryKey, category) => (
    <Box key={categoryKey} sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 1,
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
        }}
      >
        <Box sx={{ 
          color: theme.palette.primary.main, 
          display: 'flex', 
          alignItems: 'center',
          fontSize: '0.9rem'
        }}>
          {category.icon}
        </Box>
        {category.title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 0.375,
        alignItems: 'flex-start'
      }}>
        {category.filters.map(filter =>
          renderFilterChip(
            filter,
            categoryKey,
            activeFilters[categoryKey] === filter.key
          )
        )}
      </Box>
    </Box>
  );

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent sx={{ pb: isExpanded ? 2 : 1 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            p: 0.5,
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.04),
            },
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilterListIcon 
              color="primary" 
              sx={{ fontSize: '1.25rem' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '0.25px' }}>
              Filters
            </Typography>
            {activeFilterCount > 0 && (
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '12px',
                  px: 1,
                  py: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  minWidth: '20px',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {activeFilterCount}
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeFilterCount > 0 && (
              <Tooltip title="Clear all filters">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  sx={{
                    color: theme.palette.text.secondary,
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            <IconButton 
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Active Filters Summary */}
        {!isExpanded && activeFilterCount > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {Object.entries(activeFilters).map(([category, filterKey]) => {
              if (!filterKey) return null;
              
              const categoryData = filterCategories[category];
              const filterData = categoryData?.filters.find(f => f.key === filterKey);
              
              if (!filterData) return null;
              
              return (
                <Chip
                  key={`${category}-${filterKey}`}
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}>
                        {filterData.icon}
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {filterData.label}
                      </Typography>
                    </Box>
                  }
                  onDelete={() => handleFilterSelect(category, filterKey)}
                  sx={{
                    height: '24px',
                    borderRadius: '12px',
                    backgroundColor: theme.palette[filterData.color]?.main || theme.palette.primary.main,
                    color: theme.palette.common.white,
                    border: 'none',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0.25,
                    },
                    '& .MuiChip-deleteIcon': {
                      color: alpha(theme.palette.common.white, 0.8),
                      fontSize: '0.9rem',
                      '&:hover': {
                        color: theme.palette.common.white,
                      },
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Expanded Filter Options */}
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {Object.entries(filterCategories).map(([categoryKey, category]) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={categoryKey}>
                  {renderFilterCategory(categoryKey, category)}
                </Grid>
              ))}
            </Grid>
            
            {/* Action Buttons */}
            {activeFilterCount > 0 && (
              <Box sx={{ 
                mt: 3, 
                pt: 2, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                backgroundColor: alpha(theme.palette.grey[50], 0.3),
                borderRadius: '12px',
                p: 2,
                mx: -1
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    fontSize: '0.8rem'
                  }}>
                    {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleClearAll}
                    startIcon={<ClearIcon sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      borderRadius: '16px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.75,
                      border: `1.5px solid ${theme.palette.error.main}`,
                      color: theme.palette.error.main,
                      backgroundColor: 'transparent',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.common.white,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                      },
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default DefectsFilters; 